"use client";

import { AnimatePresence, m as motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { Link, usePathname } from "@/i18n/navigation";

const SERVICE_GROUPS: { key: "development" | "design" | "growth"; href: string; items: { href: string; key: string }[] }[] = [
  {
    key: "development",
    href: "/services/development",
    items: [
      { href: "/services/development/mobile-web-apps", key: "services.development.mobileWebApps.title" },
      { href: "/services/development/ecommerce", key: "services.development.ecommerce.title" },
    ],
  },
  {
    key: "design",
    href: "/services/design",
    items: [
      { href: "/services/design/brand-identity", key: "services.design.brandIdentity.title" },
      { href: "/services/design/ui-ux", key: "services.design.uiUx.title" },
      { href: "/services/design/motion", key: "services.design.motion.title" },
    ],
  },
  {
    key: "growth",
    href: "/services/growth",
    items: [
      { href: "/services/growth/marketing-seo", key: "services.growth.marketingSeo.title" },
      { href: "/services/growth/strategy", key: "services.growth.strategy.title" },
    ],
  },
];

export function NavDropdown() {
  const t = useTranslations();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Close the dropdown on every route change — same teardown-style
    // pattern as MobileMenu. Programmatic navigation (router.push) needs
    // to clear this state too, so wrapping each Link's onClick wouldn't
    // catch all paths.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, []);

  function scheduleClose() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 140);
  }
  function cancelClose() {
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border-0 bg-transparent px-[14px] py-2 text-sm text-fg2 transition-colors hover:bg-white/[0.04] hover:text-fg1"
      >
        {t("nav.services")}
        <motion.span
          aria-hidden
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block text-[10px] text-fg3"
        >
          ▾
        </motion.span>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-full left-1/2 z-50 mt-3 w-[min(640px,92vw)] -translate-x-1/2 overflow-hidden rounded-2xl border p-5 shadow-2xl"
            style={{
              borderColor: "var(--border)",
              background: "rgba(17,17,24,0.92)",
              backdropFilter: "blur(18px) saturate(140%)",
              WebkitBackdropFilter: "blur(18px) saturate(140%)",
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {SERVICE_GROUPS.map((group) => (
                <div key={group.key}>
                  <Link
                    href={group.href}
                    className="block font-display text-[15px] font-semibold text-fg1 transition-colors hover:text-brand-azure"
                  >
                    {t(`nav.dropdown.${group.key}`)}
                  </Link>
                  <ul className="mt-3 flex flex-col gap-2">
                    {group.items.map((item) => (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          className="block text-[13px] text-fg3 transition-colors hover:text-fg1"
                        >
                          {t(item.key)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div
              className="mt-5 border-t pt-4"
              style={{ borderColor: "var(--border)" }}
            >
              <Link
                href="/services"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase text-fg3 transition-colors hover:text-fg1"
                style={{ letterSpacing: "0.2em" }}
              >
                {t("nav.dropdown.view_all")}
                <span aria-hidden>→</span>
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
